import { labEntryAt, labQueueToken } from '../../constants/lab';

/**
 * labEntryDay — the IST calendar day (`YYYY-MM-DD`) an order entered the lab
 * queue. Single source of truth for "what day is this order on", used by the
 * grouping below AND by the lane sort in `LabQueueView` (T7) — the backend's
 * `lab_queue_number` resets to 1 every day per org, so sorting by token alone
 * interleaves yesterday's still-open `#L-1` with today's `#L-1`.
 *
 * The backend stamps `lab_queued_at` / `created_at` with `ist_now_iso()`
 * (`+05:30`), so this slice IS the IST calendar day — `toISOString()` is
 * BANNED here (`ist_utc_day_boundary`): it would re-bucket an order stamped
 * shortly after IST midnight into the previous UTC day.
 * @param {object} order
 * @returns {string} '' when the order has no timestamp
 */
export function labEntryDay(order) {
  return String(labEntryAt(order) || '').slice(0, 10);
}

/**
 * groupLaneByPatientDay — THE single shared selector for "a patient's open
 * tests, grouped simply" (S3). Groups an already-sorted lane by patient, then
 * by the IST calendar day the order entered the lab queue.
 *
 * Pure projection: no API call, no store read, no sort of its own — the lane
 * is already sorted (day, then queue token, then time) before this runs.
 *
 * Key = `${patient_id || patient_name}|${day}`, where `day = labEntryDay(order)`.
 *
 * Preserves the incoming order: a group's position in the returned array is
 * its FIRST order's position, so a lane can never reshuffle on refetch or on
 * a realtime `LAB_ORDER_ASSIGNED` upsert.
 *
 * @param {Array<object>} orders
 * @returns {Array<{key:string, patientName:string, queueNumber:(number|null), day:string, orders:Array<object>}>}
 */
export function groupLaneByPatientDay(orders) {
  const groups = [];
  const byKey = new Map();
  for (const o of orders || []) {
    const patientKey = o.patient_id || o.patient_name || 'Patient';
    const day = labEntryDay(o);
    const key = `${patientKey}|${day}`;
    let g = byKey.get(key);
    if (!g) {
      g = {
        key,
        patientName: o.patient_name || o.patient_id || 'Patient',
        queueNumber: labQueueToken(o),
        day,
        orders: [],
      };
      byKey.set(key, g);
      groups.push(g);
    }
    g.orders.push(o);
  }
  return groups;
}
